"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Clock, Loader as Loader2, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  id: 1,
  title: "100% Direct Consultant Access",
  desc: "You deal directly with me, Yassine. No junior staff or account managers."
}, {
  id: 2,
  title: "Actionable Strategy, Not Filler",
  desc: "A custom 30-minute breakdown focusing on technical, content, and authority quick wins."
}, {
  id: 3,
  title: "Zero Obligation or Hard Sales",
  desc: "Honest feedback on your current search performance and realistic growth projections."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border rounded-xl text-center space-y-4">
        <div className="mx-auto size-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">
          Consultation Request Received!
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {successMessage || "Thank you for booking! I will personally review your website details and email you within 24 hours to confirm our 30-minute slot."}
        </p>
        <Button type="button" variant="outline" onClick={resetForm} className="mt-2">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Your name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. Yassine Essaadi" {...field} className="bg-background text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">
              Work Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@company.com" {...field} className="bg-background text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">
              Website URL
            </FormLabel>
            <FormControl>
              <Input placeholder="https://yourwebsite.com" {...field} className="bg-background text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold py-3.5 h-auto text-base shadow-sm rounded-md transition-colors">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Reserving Your Spot...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
        <ShieldCheck className="size-4 text-primary" />
        <span>Confidential. No sales pitch. Direct consultant review.</span>
      </div>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 -z-10 size-80 rounded-full bg-muted/60 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-6 space-y-6">
            <Badge variant="secondary" className="px-3 py-1 bg-secondary text-secondary-foreground border border-border rounded-full text-xs font-medium inline-flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-accent" /> Direct Consultant Access
            </Badge>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
              Book a Free 30-Minute SEO Consultation
            </h2>

            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              When you reach out to YE Consultancy, you work directly with me—Yassine Essaadi. I will personally review your domain, identify ranking opportunities, and show you how to drive sustainable search growth without agency fluff.
            </p>

            <div className="space-y-4 pt-2">
              {benefits.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3.5">
                  <div className="mt-1 size-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-base font-default">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="p-4 rounded-xl border border-border bg-card shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3.5">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-14 rounded-full object-cover border-2 border-primary shrink-0" />
                <div>
                  <h4 className="font-semibold text-card-foreground font-serif text-base">
                    Yassine Essaadi
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Independent SEO Strategy Consultant
                  </p>
                  <a href="mailto:contact@ye-consultancy.com" className="text-xs text-primary font-medium hover:underline block mt-0.5">
                    contact@ye-consultancy.com
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:items-end gap-1.5 w-full sm:w-auto">
                <Link to="/ContactPage">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto text-xs font-medium">
                    View Full Contact Page
                  </Button>
                </Link>
                <div className="flex items-center gap-1 text-[13px] text-muted-foreground">
                  <Clock className="size-3 text-muted-foreground" />
                  <span>Response time: &lt; 24h</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-6">
            <div className="p-6 md:p-8 rounded-2xl border border-border bg-card shadow-lg relative">
              <div className="mb-6 pb-4 border-b border-border">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="text-xl font-bold font-serif text-card-foreground">
                    Request Your Free Session
                  </h3>
                  <Badge variant="outline" className="text-xs text-accent border-accent/30 bg-accent/5">
                    <SafeHydrate><Calendar className="size-3 mr-1" /></SafeHydrate> Limited Weekly Slots
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Fill in your details to reserve a 1-on-1 strategy call with Yassine.
                </p>
              </div>

              <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
              name: "",
              email: "",
              website: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              website: {
                required: true
              }
            }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I've received your request and will contact you within 24 hours to schedule our call.">
                <ConsultationFormInner />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}